import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Report, useGetScalingSize, figmaPxToWidthPercent } from '@ui/Report';
import { Icon } from '@ui/Icon/Icon';
import { ReportTableGaps } from '@ui/Report/components/ReportTable';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { ComparisonHorizontalBar } from '../ReportProductGroups/ComparisonHorizontalBar';
import { OffsetBox } from '../ReportProductGroups/OffsetBox';
import { SBEF_BAR_LIST } from '../ReportProductGroups/report-product-groups.constants';
import {
  PRODUCT_GROUPS_TEXT_COLOR,
  PRODUCT_GROUPS_LABEL_VALUE,
  SBEF_LEGEND_TEXT_FONT_SIZE,
} from '../ReportProductGroups/report-product-groups.typography';
import { PageHeaderBlock } from '../ReportProductGroups/PageHeaderBlock';
import type { TopProductsData } from './report-top-products-data.helpers';
import type { IProposal } from '@shared/models/proposals.model';

const { Page, Table, Text } = Report;

/** Bar height for the compact 15-row list — ~1/4 of the SBEF Elements bar. */
const TOP_PRODUCTS_BAR_HEIGHT = figmaPxToWidthPercent(14);

const tableGaps: ReportTableGaps = [0.75, 1.6];

/** Bar corner radius for the compact list — 1/2 of the standard SBEF bar radius. */
const TOP_PRODUCTS_BAR_RADIUS = SBEF_BAR_LIST.partRadius * 0.5;
/** Bar border width for the compact list — 1/3 of the standard SBEF bar border. */
const TOP_PRODUCTS_BAR_BORDER_WIDTH = SBEF_BAR_LIST.partBorderWidth / 3;

type ReportTopProductsProps = {
  proposalId: IProposal['id'];
  defaultTitle: string;
  color: string;
  isBaseline: boolean;
  data: TopProductsData;
  footer?: React.ReactNode;
};

export const ReportTopProducts: React.FC<ReportTopProductsProps> = ({
  proposalId,
  defaultTitle,
  color,
  isBaseline,
  data,
  footer,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize });

  return (
    <Page verticalAlign="top" footer={footer}>
      <PageHeaderBlock
        pageId="topProducts"
        proposalId={proposalId}
        defaultTitle={defaultTitle}
        isBaseline={isBaseline}
        bodyPlaceholder="The top 15 products contributing most to CO₂ emissions."
        showPlaceholderInPrint
        edgeInset={false}
      />

      <Box className={classes.list}>
        <Table templateColumns="28% 1fr" tableGaps={tableGaps}>
          {data.items.map((item) => {
            const offsetValue = item.proposalCo2e - item.baselineCo2e;
            const scale = Math.max(data.maxCo2e, 0.001);
            const maxVal = Math.max(
              item.baselineCo2e,
              item.proposalCo2e,
              0.001,
            );
            const trackWidthPct = (maxVal / scale) * 88;
            const baselinePct = (item.baselineCo2e / maxVal) * 100;
            const proposalPct = (item.proposalCo2e / maxVal) * 100;
            const iconName = getElementIconCategoryName(item.categoryId);

            return (
              <Table.Row key={item.id} alignCells="center">
                {/* Icon + Name */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: getScalingSize(0.5),
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Icon
                      category="elementCategory"
                      icon={iconName}
                      size={getScalingSize(0.8)}
                    />
                  </Box>
                  <Text
                    as="p"
                    sx={{
                      color: PRODUCT_GROUPS_TEXT_COLOR,
                      opacity: PRODUCT_GROUPS_LABEL_VALUE.opacity,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.label}
                  </Text>
                </Box>

                {/* Bar track: trackWidthPct caps longest bar at 88% leaving room for values */}
                <Box
                  sx={{
                    position: 'relative',
                    width: `${trackWidthPct}%`,
                    height: getScalingSize(TOP_PRODUCTS_BAR_HEIGHT),
                    flexShrink: 0,
                    overflow: 'visible',
                  }}
                >
                  <ComparisonHorizontalBar
                    baselinePct={baselinePct}
                    proposalPct={proposalPct}
                    color={color}
                    partRadius={TOP_PRODUCTS_BAR_RADIUS}
                    partBorderWidth={TOP_PRODUCTS_BAR_BORDER_WIDTH}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${Math.max(proposalPct, baselinePct)}%`,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: getScalingSize(PRODUCT_GROUPS_LABEL_VALUE.groupGap),
                      pl: getScalingSize(SBEF_BAR_LIST.labelGap),
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: getScalingSize(
                          PRODUCT_GROUPS_LABEL_VALUE.innerGap,
                        ),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          opacity: PRODUCT_GROUPS_LABEL_VALUE.iconOpacity,
                        }}
                      >
                        <Icon
                          category="costEmission"
                          icon="kgCO2e"
                          size={getScalingSize(
                            PRODUCT_GROUPS_LABEL_VALUE.iconSize,
                          )}
                        />
                      </Box>
                      <Text
                        as="span"
                        fontSize={SBEF_LEGEND_TEXT_FONT_SIZE}
                        fontWeight={PRODUCT_GROUPS_LABEL_VALUE.cloudFontWeight}
                        sx={{
                          color: PRODUCT_GROUPS_TEXT_COLOR,
                          fontVariantNumeric: 'tabular-nums',
                          lineHeight: PRODUCT_GROUPS_LABEL_VALUE.lineHeight,
                          opacity: PRODUCT_GROUPS_LABEL_VALUE.opacity,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getDisplayNumber(item.proposalCo2e, {
                          decimals: item.proposalCo2e >= 10 ? 0 : 1,
                        })}
                      </Text>
                    </Box>
                    {!isBaseline && offsetValue !== 0 && (
                      <OffsetBox
                        value={offsetValue}
                        label={item.label}
                        fontSize={SBEF_LEGEND_TEXT_FONT_SIZE}
                      />
                    )}
                  </Box>
                </Box>
              </Table.Row>
            );
          })}
        </Table>
      </Box>
    </Page>
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  list: {
    marginTop: getScalingSize(2.1),
  },
}));
